import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined
} from "@ant-design/icons";
import { Button, ConfigProvider, Flex, Modal, Popconfirm, Row, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Tooltip } from "antd/lib";
import { deleteUser, getAllUserRequests, updateUserSalesApprovalStatus } from "api/userData";
import { LoadingSpinner } from "components/LoadingSpinner";
import { getPaginationProps } from "components/PaginationProps";
import { SearchComponent } from "components/SearchComponent";
import { useEffect, useRef, useState } from "react";
import { COLOR_SECONDARY_BLUE } from "theme/colors";
import classes from "./User.module.css";
import { useSearchParams } from "react-router-dom";

interface DataType {
  key: string;
  name: string;
  priority: number;
  userType: string;
  roleType: string;
  email: string;
  org: string;
  credits: string;
  discount: string;
  approved: boolean;
  causeOfUse: string;
}

type UserRequestsProps = {
  search?: string | null;
};

export const UserRequests: React.FC<UserRequestsProps> = ({ search }) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tableData, setTableData] = useState<DataType[]>([]);
  const [_params, setParams] = useSearchParams();

  const selectedUserId = useRef("");

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState(search ?? "");

  const handleApprovalAction = async (id: string, status: boolean) => {
    await updateUserSalesApprovalStatus(id, {
      approved: status
    });
    fetchUsersData();
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (text) => {
        return (
          <Typography.Text
            style={{ width: "200px" }}
            ellipsis={{
              tooltip: true
            }}>
            {text}
          </Typography.Text>
        );
      }
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email"
    },
    {
      title: "Organization",
      dataIndex: "org",
      key: "org"
    },
    {
      title: "Intent of Use",
      key: "causeOfUse",
      dataIndex: "causeOfUse",
      filters: [
        {
          text: "PERSONAL",
          value: "PERSONAL"
        },
        {
          text: "PROFESSIONAL",
          value: "PROFESSIONAL"
        }
      ],
      onFilter: (value, record) =>
        record.causeOfUse.toLowerCase().includes(value.toString().toLowerCase())
    },
    {
      title: "Sign up date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (date: Date) => {
        return new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
      },
      sortDirections: ["descend", "ascend"],
      sortIcon: (sortOrder) => {
        return sortOrder.sortOrder === "descend" ? (
          <ArrowDownOutlined style={{ color: "#CECFD2" }} />
        ) : sortOrder.sortOrder === "ascend" ? (
          <ArrowUpOutlined style={{ color: "#CECFD2" }} />
        ) : (
          <></>
        );
      }
    },
    {
      title: "Action",
      key: "action",
      render: (record) => {
        return (
          <Flex>
            <Flex style={{ marginRight: "8px", marginLeft: "-12px" }}>
              <Popconfirm
                title={"Are you sure you want to approve this user?"}
                onConfirm={async () => await handleApprovalAction(record.key, true)}
                okText="Yes"
                cancelText="No">
                <Tooltip color={COLOR_SECONDARY_BLUE} title={"Approve user"} placement="top">
                  <Button style={{ color: "#34C759" }} type="text">
                    <CheckOutlined />
                  </Button>
                </Tooltip>
              </Popconfirm>
              <Popconfirm
                title="Are you sure you want to reject this user?"
                onConfirm={async () => await handleApprovalAction(record.key, false)}
                okText="Yes"
                cancelText="No">
                <Tooltip color={COLOR_SECONDARY_BLUE} title={"Reject user"} placement="top">
                  <Button style={{ color: "#B42F26" }} type="text">
                    <CloseOutlined />
                  </Button>
                </Tooltip>
              </Popconfirm>
            </Flex>
          </Flex>
        );
      }
    }
  ];

  const fetchUsersData = async () => {
    try {
      const res = await getAllUserRequests();
      const data = res.map((item: any) => ({
        ...item,
        key: item.id,
        name: `${item.firstName} ${item.lastName ?? ""}`,
        email: item.email,
        org: item.organization,
        approved: item.approved,
        roleType: item?.role?.type
      }));

      setTableData(data);
    } catch (error) {
      // console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    await deleteUser(selectedUserId.current);
    await fetchUsersData();
    setDeleteModalOpen(false);
  };

  useEffect(() => {
    fetchUsersData();
  }, []);

  if (isLoading) return <LoadingSpinner />;

  return (
    <>
      <div>
        <Typography.Title style={{ fontWeight: "normal", fontSize: "30px" }} level={1}>
          Signup requests
        </Typography.Title>

        <SearchComponent
          searchQuery={searchQuery}
          setSearchQuery={(q) => {
            setSearchQuery(q);
            setParams((params) => {
              params.set("search", q);
              return params;
            });
          }}
          placeholder="Search by name, email, or organization"
        />

        <Table
          columns={columns}
          dataSource={tableData.filter((user: any) => {
            return (
              user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              user.org?.toLowerCase().includes(searchQuery.toLowerCase())
            );
          })}
          style={{ marginTop: "30px" }}
          scroll={{ x: "max-content" }}
          pagination={getPaginationProps()}
          onChange={(_: any, fil: any, sorter) => {
            const { order, columnKey } = sorter as { order: string; columnKey: keyof DataType };
            if (order && columnKey) {
              const sortedData = tableData.sort((a, b) => {
                const date1 = new Date(a[columnKey] as string);
                const date2 = new Date(b[columnKey] as string);
                // sort based on dates
                if (order === "ascend") {
                  return date1.getTime() - date2.getTime();
                } else {
                  return date2.getTime() - date1.getTime();
                }
              });
              setTableData([...sortedData]);
            }
          }}
        />
        <ConfigProvider
          theme={{
            token: {
              colorBgBase: "#202440"
            },
            components: {
              Modal: {
                contentBg: "#202440",
                colorText: "#fff"
              }
            }
          }}>
          <Modal
            open={deleteModalOpen}
            closable={false}
            maskClosable
            okText="Yes"
            cancelText="No"
            okButtonProps={{
              disabled: isLoading,
              loading: isLoading
            }}
            onCancel={() => setDeleteModalOpen(false)}
            onOk={handleDelete}
            cancelButtonProps={{
              style: {
                border: "none"
              }
            }}
            width={300}
            styles={{
              footer: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              },
              content: {
                padding: "12px"
              }
            }}>
            <Row className={classes.logoutTitle} justify="center">
              Delete User
            </Row>
            <Row className={classes.subTitle}>Are you sure you want to delete this user?</Row>
          </Modal>
        </ConfigProvider>
      </div>
    </>
  );
};
